import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for scroll-based animations
 * @param {string} animationType - Type of animation: 'fade-in-up', 'fade-in-down', 'fade-in-left', 'fade-in-right', 'fade-in', 'scale-in', 'bounce-in'
 * @param {number} threshold - Percentage of element visibility to trigger animation (0-1)
 * @param {number} delay - Delay in milliseconds before animation starts
 */
const useScrollAnimation = (animationType = 'fade-in-up', threshold = 0.1, delay = 0) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          if (delay > 0) {
            setTimeout(() => {
              setIsVisible(true);
            }, delay);
          } else {
            setIsVisible(true);
          }
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -50px 0px', // Trigger slightly before element comes into view
      }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, delay, isVisible]);

  const animationClass = isVisible ? `animate-${animationType}` : 'scroll-reveal';

  return { ref, animationClass, isVisible };
};

export default useScrollAnimation;
